<template>
  <div>
    <v-row dense>
      <v-col cols="12">
        <div class="d-flex align-center text-orange-accent-4 mt-3">
          <v-icon icon="mdi-alert" color="orange-accent-4" class="mr-2" />
          <span> Notice: Be sure to save after updating values for it to be reflected. </span>
        </div>
      </v-col>
    </v-row>

    <!-- Inline Table View -->
    <v-row dense>
      <v-col>
        <v-text-field
          v-model="samlIdpSearch"
          label="Search"
          append-icon="mdi-magnify"
          variant="underlined"
          color="primary"
        />
      </v-col>
      <v-col cols="auto" class="d-flex justify-end">
        <cs-button
          primary
          label="Add SAML External IDP"
          aria-label="Add SAML External IDP"
          class="mt-4"
          left-icon="mdi-plus-circle"
          :disabled="isFormReadOnly"
          @click="onAddClick"
        />
      </v-col>
    </v-row>
    <v-data-table
      :items="inputFields"
      height="400"
      fixed-header
      density="compact"
      :search="samlIdpSearch"
      :loading="fetchHubCsvExternalIdpSamlInProgress"
      :headers="tableHeaders"
    >
      <template #[`item.tenantSubDomain`]="{ item }">
        {{ item.tenantSubDomain || '-' }}
      </template>
      <template #[`item.metadataType`]="{ item }">
        {{ item.metadataType || '-' }}
      </template>
      <template #[`item.providerName`]="{ item }">
        {{ item.providerName || '-' }}
      </template>
      <template #[`item.idpSignOut`]="{ item }">
        {{ item.idpSignOut != null ? String(item.idpSignOut).toUpperCase() : '-' }}
      </template>
      <template #[`item.actions`]="{ item }">
        <v-icon
          icon="mdi-pencil"
          color="primary"
          aria-label="Edit row"
          :disabled="isFormReadOnly"
          class="mr-2"
          @click="onEditRowClick(item)"
        />
        <v-icon
          icon="mdi-delete"
          color="primary"
          aria-label="Delete row"
          :disabled="isFormReadOnly"
          @click="onDeleteClick(item)"
        />
      </template>
    </v-data-table>
    <v-row dense class="mt-4">
      <v-col cols="12" class="d-flex justify-end">
        <cs-button
          primary
          label="Save"
          aria-label="Save SAML configurations"
          :loading="saveInProgress"
          :disabled="isFormReadOnly"
          @click="onConfirmationClick()"
        />
      </v-col>
    </v-row>

    <!-- Form Dialog (Add/Edit SAML External IDP) -->
    <cs-form-dialog
      id="dlg-client-hub-external-idp-saml-config-form"
      v-model="formDialogDisplayed"
      body-id="dlg-client-hub-external-idp-saml-config-form-body"
      :heading="editingIndex !== null ? 'Edit SAML External IDP' : 'Add SAML External IDP'"
      :primary-action="{
        label: editingIndex !== null ? 'Update' : 'Save',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="onFormSubmit()"
      @secondary1-click="onFormCancel()"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formFields.tenantSubDomain"
                label="Tenant SubDomain"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.tenantSubDomain"
                @blur="v$.formFields.tenantSubDomain.$touch()"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="formFields.metadataType"
                label="Metadata Type"
                variant="underlined"
                color="primary"
                :items="metadataTypeOptions"
                :error-messages="formErrors.metadataType"
                @update:model-value="onMetadataTypeChange"
                @blur="v$.formFields.metadataType.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-if="formFields.metadataType === 'File'"
                v-model="formFields.metadataFileName"
                label="Metadata File Name"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.metadataFileName"
                @update:model-value="convertToLowerCase"
                @blur="v$.formFields.metadataFileName.$touch()"
              />
              <v-text-field
                v-if="formFields.metadataType === 'URL'"
                v-model="formFields.metadataUrl"
                label="Metadata URL"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.metadataUrl"
                @blur="v$.formFields.metadataUrl.$touch()"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="formFields.idpSignOut"
                label="IDP Sign Out"
                variant="underlined"
                color="primary"
                :items="idpSignOutOptions"
                :error-messages="formErrors.idpSignOut"
                @blur="v$.formFields.idpSignOut.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-select
                v-model="formFields.encryptedResponses"
                label="Encrypted Responses"
                variant="underlined"
                color="primary"
                :items="encryptedResponsesOptions"
                :error-messages="formErrors.encryptedResponses"
                @blur="v$.formFields.encryptedResponses.$touch()"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="formFields.signedRequest"
                label="Signed Request"
                variant="underlined"
                color="primary"
                :items="signedRequestOptions"
                :error-messages="formErrors.signedRequest"
                @blur="v$.formFields.signedRequest.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="formFields.attributeMappings"
                label="Attributes Mapping"
                variant="underlined"
                color="primary"
                placeholder="Type hub-attr1:saml-attr1 | hub-attr2:saml-attr2 and press Enter"
                :hint="attributeMappingsHint"
                persistent-hint
                :error-messages="formErrors.attributeMappings"
                @update:model-value="v$.formFields.attributeMappings.$touch()"
                @blur="v$.formFields.attributeMappings.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="formFields.providerName"
                label="Provider Name"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.providerName"
                @blur="v$.formFields.providerName.$touch()"
              />
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <!-- Confirmation Dialog -->
    <cs-form-dialog
      id="dlg-client-hub-external-idp-saml-config-confirmation"
      v-model="showConfirmationDialog"
      heading="Save Confirmation"
      body-id="dlg-client-hub-external-idp-saml-config-confirmation-body"
      :primary-action="{
        label: 'Continue',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="onConfirmAndSave()"
      @secondary1-click="showConfirmationDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>
                  <b>Notice:</b> Adding or updating a record will remove the currently uploaded XML
                  file. You will need to re-upload the file after the record is added or updated to
                  ensure the data matches.
                </p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <!-- Success Dialog -->
    <cs-form-dialog
      id="dlg-client-hub-external-idp-saml-config-save-success"
      v-model="showSaveSuccessDialog"
      heading="Save Success"
      body-id="dlg-client-hub-external-idp-saml-config-save-success-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showSaveSuccessDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>The SAML External IDP information has been saved successfully.</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-external-idp-saml-config-validation-error"
      v-model="showValidationErrorDialog"
      heading="Validation Error"
      body-id="dlg-client-hub-external-idp-saml-config-validation-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showValidationErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p class="text-red-accent-4">{{ validationErrorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <!-- Error Dialog -->
    <cs-form-dialog
      id="dlg-client-hub-external-idp-saml-config-save-error"
      v-model="showSaveErrorDialog"
      heading="Save Error"
      body-id="dlg-client-hub-external-idp-saml-config-save-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showSaveErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>
                  There is some error while saving the SAML External IDP information. Please try
                  again.
                </p>
                <p class="text-red-accent-4">{{ errorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import { helpers, required, minLength, maxLength } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import * as types from '@/store/mutationTypes';
import { validateProviderName as validateProviderNameHelper } from '@/components/validation/validateProviderName';
import {
  validateAttributeMappings,
  formatAttributeMappings
} from '@/components/validation/validateAttributeMappings';

const FIELD_MAP = {
  tenantSubDomain: 'TenantSubDomain',
  metadataType: 'MetadataType',
  metadataFileName: 'MetadataFileName',
  metadataUrl: 'MetadataUrl',
  idpSignOut: 'IdpSignOut',
  encryptedResponses: 'EncryptedResponses',
  signedRequest: 'SignedRequest',
  attributeMappings: 'AttributeMappings',
  providerName: 'ProviderName'
};

const mapSamlIdpFields = (source, isFormatToApi = false) => {
  const entries = Object.entries(FIELD_MAP);
  if (isFormatToApi) {
    const formatted = Object.fromEntries(entries.map(([ui, api]) => [api, source[ui]]));
    formatted.AttributeMappings = formatAttributeMappings(formatted.AttributeMappings);
    return formatted;
  }

  const base = Object.fromEntries(entries.map(([ui, api]) => [ui, source[api]]));
  base.idpSignOut = base.idpSignOut ? String(base.idpSignOut).toUpperCase() : null;
  base.encryptedResponses = base.encryptedResponses
    ? String(base.encryptedResponses).toUpperCase()
    : null;
  base.signedRequest = base.signedRequest ? String(base.signedRequest).toUpperCase() : null;
  return base;
};

const DEFAULT_FORM_FIELDS = {
  tenantSubDomain: '',
  metadataType: 'URL',
  metadataFileName: '',
  metadataUrl: '',
  idpSignOut: null,
  encryptedResponses: null,
  signedRequest: null,
  attributeMappings: '',
  providerName: ''
};

export default {
  name: 'ClientHubExternalIdpSamlConfig',

  props: {
    clientSid: { type: String, required: false, default: '' },
    uploadFileInProgress: { type: Boolean, required: false, default: false },
    hasUploadedXmlFile: { type: Boolean, required: false, default: false }
  },

  emits: ['save-success'],

  setup: () => ({ v$: useVuelidate() }),

  data() {
    return {
      attributeMappingsHint:
        'Required: email, family_name, given_name, preferred_username. (e.g. preferred_username:upn | email:mail)',
      samlIdpSearch: '',
      tableHeaders: [
        { title: 'Tenant SubDomain', key: 'tenantSubDomain' },
        { title: 'Metadata Type', key: 'metadataType' },
        { title: 'Provider Name', key: 'providerName' },
        { title: 'IDP Sign Out', key: 'idpSignOut' },
        { title: 'Actions', key: 'actions', align: 'center' }
      ],
      formDialogDisplayed: false,
      inputFields: [],
      editingIndex: null,
      formFields: cloneDeep(DEFAULT_FORM_FIELDS),
      metadataTypeOptions: ['File', 'URL'],
      idpSignOutOptions: ['TRUE', 'FALSE'],
      encryptedResponsesOptions: ['TRUE', 'FALSE'],
      signedRequestOptions: ['TRUE', 'FALSE'],
      saveInProgress: false,
      showSaveSuccessDialog: false,
      showSaveErrorDialog: false,
      showValidationErrorDialog: false,
      validationErrorMessage: '',
      errorMessage: '',
      showConfirmationDialog: false,
      fetchHubCsvExternalIdpSamlInProgress: false,
      fetchHubCsvExternalIdpSamlErrorMessage: '',
      fetchHubCsvExternalIdpSamlErrorDialog: false
    };
  },

  validations() {
    return {
      formFields: {
        tenantSubDomain: {
          required: helpers.withMessage('Tenant subdomain is required', required),
          minLength: helpers.withMessage(
            'Tenant subdomain must be at least 2 characters',
            minLength(2)
          ),
          maxLength: helpers.withMessage(
            'Tenant subdomain cannot be more than 31 characters',
            maxLength(31)
          ),
          pattern: helpers.withMessage(
            'Lowercase letters, numbers, and hyphens only. Must start and end with a letter or number.',
            (v) => !v || /^[a-z0-9](?:[a-z0-9-]{0,29}[a-z0-9])?$/.test(v)
          )
        },
        metadataType: {
          required: helpers.withMessage('Metadata type is required', required),
          validType: helpers.withMessage(
            'Metadata type must be one of File, URL',
            (v) => !v || ['File', 'URL'].includes(v)
          )
        },
        metadataFileName: {
          requiredIfFile: helpers.withMessage(
            'Metadata file name is required',
            (v) => this.formFields.metadataType !== 'File' || !!v
          ),
          patternIfFile: helpers.withMessage(
            'Must be a valid XML file name.',
            (v) => this.formFields.metadataType !== 'File' || !v || /\.xml$/i.test(v)
          )
        },
        metadataUrl: {
          requiredIfUrl: helpers.withMessage(
            'Metadata URL is required',
            (v) => this.formFields.metadataType !== 'URL' || !!v
          ),
          maxLengthIfUrl: helpers.withMessage(
            'Metadata URL cannot be more than 255 characters',
            (v) => this.formFields.metadataType !== 'URL' || !v || v.length <= 255
          ),
          validUrl: helpers.withMessage('Metadata URL must be a valid URL using HTTPS', (v) => {
            if (this.formFields.metadataType !== 'URL' || !v) return true;
            try {
              const url = new URL(v);
              return url.protocol === 'https:';
            } catch {
              return false;
            }
          })
        },
        idpSignOut: {
          required: helpers.withMessage(
            'IDP sign out is required',
            (v) => v !== null && v !== undefined && v !== ''
          ),
          validValue: helpers.withMessage(
            'IDP sign out must be one of TRUE, FALSE',
            (v) => (!v && v !== 0) || ['TRUE', 'FALSE'].includes(v)
          )
        },
        encryptedResponses: {
          required: helpers.withMessage(
            'Encrypted responses is required',
            (v) => v !== null && v !== undefined && v !== ''
          ),
          validValue: helpers.withMessage(
            'Encrypted responses must be one of TRUE, FALSE',
            (v) => (!v && v !== 0) || ['TRUE', 'FALSE'].includes(v)
          )
        },
        signedRequest: {
          required: helpers.withMessage(
            'Signed request is required',
            (v) => v !== null && v !== undefined && v !== ''
          ),
          validValue: helpers.withMessage(
            'Signed request must be one of TRUE, FALSE',
            (v) => (!v && v !== 0) || ['TRUE', 'FALSE'].includes(v)
          )
        },
        attributeMappings: {
          validateMappings: helpers.withMessage(
            () => {
              const result = validateAttributeMappings(
                this.formFields.attributeMappings,
                'saml',
                this.attributeMappingsHint
              );
              return typeof result === 'string'
                ? result
                : `Invalid attribute mappings: ${this.attributeMappingsHint}`;
            },
            (v) => validateAttributeMappings(v, 'saml', this.attributeMappingsHint) === true
          )
        },
        providerName: {
          validateProviderName: helpers.withMessage(
            () => {
              const result = this.validateProviderName(this.formFields.providerName);
              return typeof result === 'string' ? result : 'Provider name validation failed';
            },
            (v) => {
              const result = this.validateProviderName(v);
              return result === true;
            }
          ),
          required: helpers.withMessage(
            'Provider Name is required',
            (v) => v !== null && v !== undefined && v !== ''
          )
        }
      }
    };
  },

  computed: {
    isFormReadOnly() {
      return (
        this.saveInProgress ||
        this.uploadFileInProgress ||
        this.fetchHubCsvExternalIdpSamlInProgress
      );
    },
    formErrors() {
      const errors = {};
      if (!this.v$.formFields) return errors;
      Object.entries(this.v$.formFields).forEach(([key, field]) => {
        if (!field.$error) {
          errors[key] = '';
        } else {
          const error = field.$errors[0];
          errors[key] = error?.$message || error?.$validator || '';
        }
      });
      return errors;
    }
  },

  watch: {
    'formFields.tenantSubDomain'() {
      if (this.v$.formFields.providerName.$dirty) {
        this.v$.formFields.providerName.$touch();
      }
    }
  },

  async mounted() {
    await this.fetchHubCsvExternalIdpSaml();
  },

  methods: {
    onDeleteClick(item) {
      const index = this.inputFields.findIndex(
        (f) => f.tenantSubDomain === item.tenantSubDomain && f.providerName === item.providerName
      );
      if (index !== -1) this.inputFields.splice(index, 1);
    },

    onAddClick() {
      this.editingIndex = null;
      this.formFields = cloneDeep(DEFAULT_FORM_FIELDS);
      this.v$.$reset();
      this.formDialogDisplayed = true;
    },

    onEditRowClick(item) {
      const index = this.inputFields.findIndex(
        (f) => f.tenantSubDomain === item.tenantSubDomain && f.providerName === item.providerName
      );
      this.editingIndex = index;
      this.formFields = cloneDeep(this.inputFields[index]);
      this.v$.$reset();
      this.formDialogDisplayed = true;
    },

    async onFormSubmit() {
      const isValid = await this.v$.$validate();
      if (!isValid) return;

      if (this.editingIndex !== null) {
        this.inputFields[this.editingIndex] = cloneDeep(this.formFields);
      } else {
        this.inputFields.push(cloneDeep(this.formFields));
      }
      this.formDialogDisplayed = false;
    },

    onFormCancel() {
      this.v$.$reset();
      this.formDialogDisplayed = false;
    },

    convertToLowerCase(v) {
      this.formFields.metadataFileName = v.toLowerCase();
    },

    validateProviderName(v) {
      return validateProviderNameHelper({
        inputFields: this.inputFields,
        editingIndex: this.editingIndex,
        tenantSubDomain: this.formFields.tenantSubDomain
      })(v);
    },

    onMetadataTypeChange() {
      this.formFields.metadataFileName = '';
      this.formFields.metadataUrl = '';
    },

    async onConfirmationClick() {
      if (this.hasUploadedXmlFile) {
        this.showConfirmationDialog = true;
        return;
      }

      await this.onSaveClick();
    },

    async onConfirmAndSave() {
      this.showConfirmationDialog = false;
      await this.onSaveClick();
    },

    async onSaveClick() {
      if (this.inputFields.length === 0) {
        this.showValidationErrorDialog = true;
        this.validationErrorMessage =
          'At least one SAML External IDP record(s) is required to save.';
        return;
      }

      try {
        if (this.saveInProgress) return;
        this.saveInProgress = true;

        const samlIdpInfo = this.inputFields.map((item) => mapSamlIdpFields(item, true));

        await this.$store.dispatch('hub-migration/postCsvExternalIdpSaml', {
          clientSid: this.clientSid,
          samlIdpInfo
        });
        this.showSaveSuccessDialog = true;
        this.$emit('save-success');
        await this.fetchHubCsvExternalIdpSaml();
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || error.message;
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.showSaveErrorDialog = true;
      } finally {
        this.saveInProgress = false;
      }
    },

    async fetchHubCsvExternalIdpSaml() {
      if (this.fetchHubCsvExternalIdpSamlInProgress) return;
      this.fetchHubCsvExternalIdpSamlInProgress = true;

      try {
        const externalIdpSaml = await this.$store.dispatch('hub-migration/getCsvExternalIdpSaml', {
          clientSid: this.clientSid
        });
        this.inputFields = (externalIdpSaml?.samlIdpInfo || []).map((item) =>
          mapSamlIdpFields(item)
        );
      } catch (err) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.fetchHubCsvExternalIdpSamlErrorMessage = err?.response?.data?.message || err.message;
        this.fetchHubCsvExternalIdpSamlErrorDialog = true;
      } finally {
        this.fetchHubCsvExternalIdpSamlInProgress = false;
      }
    }
  }
};
</script>
